import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Seo from "@/components/feature/Seo";
import { breadcrumbJsonLd } from "@/lib/seo";
import { pageMeta } from "@/lib/pageMeta";
import { laAttractions } from "@/mocks/laAttractions";

const jsonLd = [
  breadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "LA Tours", path: "/tours" },
  ]),
  {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: "Private Los Angeles City Tour",
    description:
      "Customizable private tours of Los Angeles with a local driver-guide: Hollywood Sign, Griffith Observatory, Venice Beach, Beverly Hills, Santa Monica, and Downtown LA.",
    touristType: ["Tourists", "Business travelers", "Families"],
    itinerary: laAttractions.map((a) => ({
      "@type": "TouristAttraction",
      name: a.name,
      description: a.description,
    })),
  },
];

export default function Tours() {
  return (
    <PageLayout
      eyebrow="LA Tour Guide"
      title={
        <>
          Private Los Angeles Tours
          <br />
          <span className="italic text-foreground-500 font-normal">with a Local Guide</span>
        </>
      }
      intro="Skip the tour bus. Explore Hollywood, Venice Beach, Beverly Hills, and LA's hidden gems in a private vehicle with a driver who knows every photo spot, shortcut, and sunset view. Fully customizable itineraries."
    >
      <Seo
        title={pageMeta.tours.title}
        description={pageMeta.tours.description}
        path={pageMeta.tours.path}
        jsonLd={jsonLd}
      />
      <section className="px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {laAttractions.map((attraction) => (
            <article
              key={attraction.id}
              className="bg-background-50 rounded-2xl border border-background-200/60 overflow-hidden flex flex-col"
            >
              <img
                src={attraction.image}
                alt={`${attraction.name}, private tour stop in Los Angeles`}
                loading="lazy"
                className="w-full aspect-[4/3] object-cover object-top"
              />
              <div className="p-5 flex flex-col flex-1">
                <span className="text-xs font-medium text-primary-600 tracking-widest uppercase mb-2">
                  {attraction.category}
                </span>
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                  {attraction.name}
                </h2>
                <p
                  className="text-foreground-600 text-sm leading-relaxed flex-1"
                  dangerouslySetInnerHTML={{ __html: attraction.description }}
                />
              </div>
            </article>
          ))}
        </div>
        <div className="max-w-7xl mx-auto mt-8 text-center">
          <p className="text-foreground-600 text-sm mb-4">
            Want a fully custom itinerary? Tell us where you&rsquo;d like to go.
          </p>
          <Link
            to="/#reservation"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <span>Plan My Private Tour</span>
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}
